import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, EventEmitter, Optional } from "@angular/core";
import { FormGroup } from "@angular/forms";
//import { GridOptions, GridApi, AllModules } from '@ag-grid-enterprise/all-modules';
import { GridOptions, GridApi } from "ag-grid-community";

import { Observable, Subscription, Subject } from "rxjs";

import { BaseControlComponent } from "../base-control/base-control.component";

import { CasingConstructionRulesService } from "./services/casing-construction-rules.service";
import { CasingPlanAlertService } from "./services/casing-plan-alert.service";
import { CoordinateSystem, GisLocationService } from "../../../gis/services/gis-location.service";
import { DialogService } from "../../../common/services/dialog.service";
import { DocumentManagementService, IAzureBlobSasToken, IUploadFile } from "../../../common/services/document-management.service";
import { LoaderService } from "../../../common/services/loader.service";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";

import { Alert, AlertType } from "./models/Alerts.model";
import { AlertStatus, CasingPlanModel, CasingPlanSteps, CasingType, ensureWellComponentConfig, FormationModel, getContextLocation, getMultipleReadContextLocations, SegmentWizardSteps, StatePlane, WellConfigType, WellConfigurationIndex, WellDirection, WellSegmentGisDataModel, WellSegmentModel, CasingPlanComponent } from "./models/casing-plan.model";
import { deepClone } from "../../../builder/models/editor.model";
import { Button } from "../../../flow/models/flow.model";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { booleanToYesNo, hasBorehole, hasCasing, hasCement, isDefined, typeTransformer } from "./models/casing-plan-functions.model";
import { ContextInteractionService } from "../../services/context-interaction.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";

@Component({
    selector: "app-new-casing-plan",
    templateUrl: "./new-casing-plan.component.html",
    styleUrls: ["./new-casing-plan.component.scss"],
    providers: [CasingConstructionRulesService, CasingPlanAlertService, GisLocationService, FlowInstanceService, DialogService],
    standalone: false
})

export class NewCasingPlanComponent extends BaseControlComponent implements OnInit, OnDestroy, CasingPlanComponent {
    static componentName: string = "NewCasingPlanComponent";
    
    activeSegmentIndex: number = 0;
    alertList: Alert[] = [];
    alerts: Subscription;
    blockAdvance: boolean = false;
    currentWellSegment: WellSegmentModel;
    observableAlerts: Subscription;
    observableAlertList: Alert[] = [];
    canBeCompleted: string = "no";
    completionEligible: Subscription;
    casingPlan: CasingPlanModel;
    currentStep: CasingPlanSteps = CasingPlanSteps.Introduction;    
    formations: FormationModel[];
    fullViewSteps = [ CasingPlanSteps.Introduction, CasingPlanSteps.Regulations, CasingPlanSteps.AlertsOverview];
    gisColumnDefsNoEdit = [
        {
            headerName: "Point",
            field: "point"
        },
        {
            headerName: "Measured Depth (ft)",
            field: "md"
        },
        {
            headerName: "Inclination (&#176;)",
            field: "inclination"
        },
        {
            headerName: "Azimuth (&#176;)",
            field: "azimuth"
        },
        {
            headerName: "True Vertical Depth (ft)",
            field: "tvd"
        },
        {
            headerName: "VertSect (ft)",
            field: "vertSect"
        },
        {
            headerName: "Latitude",
            field: "latitude",
            tooltipField: "latitude"
        },
        {
            headerName: "Longitude",
            field: "longitude",
            tooltipField: "longitude"
        }
    ];
    gisGridApi: GridApi;
    idx: number;
    isComplete: boolean = false;
    isNewWell: boolean;
    latitude: number;
    longitude: number;
    nad83Y: number;
    nad83X: number;
    previousStep: CasingPlanSteps = undefined;
    previousSubstep: SegmentWizardSteps = undefined;
    previousWellSegment: WellSegmentModel;
    proposedFormations: string[] = [];
    saveInProgress = new EventEmitter<boolean>(false);
    segmentChanged = new EventEmitter<WellSegmentModel>();
    segmentQueued: boolean | undefined;
    statePlane: CoordinateSystem;    
    steps = CasingPlanSteps;
    substep: SegmentWizardSteps = undefined;
    wellType: string;
    wellTypeDescription: string;

    get activePosition(): string {
        return this.getPosition(this.activeSegmentIndex);
    }

    get affirmComplete(): boolean {
        return this.casingPlan.affirmedComplete;
    }

    set affirmComplete(value: boolean) {
        this.casingPlan.affirmedComplete = value;
        this.saveContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.IsComplete), this.casingPlan.affirmedComplete);
    }

    get applicationData(): { applicationNumber: string, wellName: string, wellNumber: string } {
        return { applicationNumber: this.applicationNumber, wellName: this.wellName, wellNumber: this.wellNumber };
    }

    get applicationNumber(): string {
        if (this.isEdit) return;
        return this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.ApplicationNumber));
    }

    get disableNext(): boolean {
        let result = false;
        if (this.currentStep === CasingPlanSteps.WellSegmentWizard) {
            switch (this.substep) {
                case SegmentWizardSteps.Casing:
                    if (this.formGroup) {
                        result = !this.formGroup.valid || this.currentWellSegment.casingType.code === "-1";
                    } else {
                        result = (this.currentWellSegment.casingType.definition === undefined || this.currentWellSegment.casingType.code === "-1");
                    }
                    break;
                case SegmentWizardSteps.Conditions:
                    if (this.formGroup) {
                        result = !this.formGroup.valid || this.currentWellSegment.casingCondition.id === "-1";
                    } else {
                        result = (this.currentWellSegment.casingCondition.name === undefined || this.currentWellSegment.casingCondition.id === "-1");
                    }
                    break;
                case SegmentWizardSteps.Tools:
                    if (this.formGroup) {
                        result = !this.formGroup.valid || this.currentWellSegment.drillingEquipment.code === "-1";
                    } else {
                        result = (this.currentWellSegment.drillingEquipment.definition === undefined || this.currentWellSegment.drillingEquipment.code === "-1");
                    }
                    break;
                case SegmentWizardSteps.Points:
                    if (this.formGroup) {
                        result = !this.formGroup.valid;
                    } else {
                        result = false;
                    }
                    break;
                case SegmentWizardSteps.AngleBuilder:
                    const startIndex = this.currentWellSegment.gisObjects.findIndex(p => p.point.toLowerCase() === "start");
                    const endIndex = this.currentWellSegment.gisObjects.findIndex(p => p.point.toLowerCase() === "end");
                    const missingDepth = this.currentWellSegment.gisObjects.findIndex(p => (p.point.toLowerCase() !== "start" && p.tvd === 0) || p.latitude === undefined || p.longitude === undefined);
                    result = !(startIndex !== -1 && endIndex !== -1 && missingDepth === -1);
                    break;
                case SegmentWizardSteps.ReviewWellSegment:
                    result = this.segmentQueued === undefined && !this.affirmComplete && this.activeSegmentIndex == this.casingPlan.wellSegments.length - 1;
                    break;
                default:
                    result = false;
                    break;
            }
        } else if (this.currentStep === CasingPlanSteps.Introduction) {
            result = !this.understandConfirm;
        } else if (this.currentStep === CasingPlanSteps.ReviewCasingPlan) {
            result = this.showNoAnglesWarning || !this.affirmComplete;
        }
        return result || this.blockAdvance;
    }

    get informationalAlerts(): number {
        let total: number = 0;
        if (this.alertList) {
            const filtered = this.alertList.filter(a => a.alertType === AlertType.Information && a.applicable);
            total = filtered.length;
        }
        return total;
    }

    get resolvedAlerts(): number {
        let total: number = 0;
        if (this.alertList) {
            const filtered = this.alertList.filter(a => a.alertType !== AlertType.Information && a.resolved && a.applicable);
            total = filtered.length;
        }
        return total;
    }

    get requireAngles(): boolean {
        let required: boolean = false;
        if (this.casingPlan.wellSegments.length > 0) {
            for (let s = 0; s < this.activeSegmentIndex; s++) {
            required = this.casingPlan.wellSegments[s].gisObjects.findIndex(sg => sg.point === "Kick Point") !== -1;
            if (required) break;
            }
        }
        return required;
    }

    get showFullView(): boolean {
        return this.fullViewSteps.findIndex(f => f === this.currentStep) !== -1;
    }

    get showNoAnglesWarning(): boolean {
        let result = false;
        if (this.wellDirection === WellDirection.Horizontal || this.wellDirection === WellDirection.OtherHorizontal) {
            let hasAngle: boolean = false;
            for (let s = 0; s < this.casingPlan.wellSegments.length; s++) {
            hasAngle = this.casingPlan.wellSegments[s].gisObjects.findIndex(g => g.point.toLowerCase() === "kick point") !== -1;
            if (hasAngle) break;
            }
            result = !hasAngle;
        }
        return result;
    }

    get showPrecis(): boolean {
        let show = false;
        if (this.currentStep === CasingPlanSteps.WellSegmentWizard) {
            switch (this.substep) {
                case SegmentWizardSteps.ReviewWellSegment:
                    show = false;
                    break
                case SegmentWizardSteps.Casing:
                    show = isDefined(this.currentWellSegment.casingType) && isDefined(this.currentWellSegment.casingType.definition);
                    break;
                default:
                    show = true;
                    break;
            }            
        }
        return show;
    }

    private _understandConfirm: boolean;
    get understandConfirm(): boolean {
        if (this.casingPlan) {
            this._understandConfirm = this.casingPlan.regulationsConfirmed;
        }
        return this._understandConfirm;
    }

    set understandConfirm(value: boolean) {
        this._understandConfirm = value;
        if (this.casingPlan) {
            this.casingPlan.regulationsConfirmed = value;
        }
    }

    get unresolvedAlerts(): number {
        let total: number = 0;
        if (this.alertList) {
            const filtered = this.alertList.filter(a => a.alertType !== AlertType.Information && !a.resolved && a.applicable);
            total = filtered.length;
        }
        return total;
    }

    get wellName(): string {
        if (this.isEdit) return "";
        return this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellName));
    }

    get wellNumber(): string {
        if (this.isEdit) return "";
        return this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellNumber));
    }

    get wellDirection(): string {
        return this.casingPlan.wellDirection;
    }

    set wellDirection(value: string) {
        this.casingPlan.wellDirection = value as WellDirection;
    }

    constructor(
        public readonly location: GisLocationService,
        @Optional() public readonly buttonOverride: FlowButtonOverrideService,
        public readonly constructionService: CasingConstructionRulesService,
        public readonly alertsService: CasingPlanAlertService,
        public readonly dialogService: DialogService,
        protected readonly documentManagementService: DocumentManagementService,
        public readonly loader: LoaderService,
		public readonly flowInstanceService: FlowInstanceService,
		private readonly _formatter: GridFormatterService,
        public readonly contextService: ContextInteractionService) {
		super(contextService);
    }

    ngOnDestroy() {
        if (this.buttonOverride)
            this.buttonOverride.resetOverride();
        if (this.completionEligible) {
            this.completionEligible.unsubscribe();
        }
    }

    ngOnInit() {
        if (this.componentData.label && this.componentData.label.toLocaleLowerCase() === "form actions") {
            this.componentData.label = "Casing and Cementing Plans"
        }
        super.ngOnInit();
        if (!this.isEdit) {
            if (this.buttonOverride) {
                this.buttonOverride.incrementFunction = this._incrementFunction.bind(this);
                this.buttonOverride.decrementFunction = this._decrementFunction.bind(this);
                this.buttonOverride.setValidatorNextButton(() => { return this.disableNext; });
                
            }
            if (!this.componentData.config) {
                this.componentData.config = { wellDataPoints: ensureWellComponentConfig(WellConfigType.NewCasingPlan, []) };
            } else {
                this.componentData.config.wellDataPoints = ensureWellComponentConfig(WellConfigType.NewCasingPlan, this.componentData.config.wellDataPoints);
            }
            const storedData = this.getContextValue();
            if (!storedData) {
                this.casingPlan = new CasingPlanModel();
                this.setContextValue(this.casingPlan);
            } else {                
                this.casingPlan = storedData as CasingPlanModel;
                if (this.casingPlan.wellSegments.length > 0 && this.casingPlan.wellSegments.findIndex(s => isDefined(s.casingType) && !isDefined(s.casingType.code) && isDefined((s.casingType as any).Code)) !== -1) {
                    const segments: WellSegmentModel[] = [];
                    for (let c = 0; c < this.casingPlan.wellSegments.length; c++) {
                        const segment = deepClone(this.casingPlan.wellSegments[c]);
                        const type = typeTransformer(segment.casingType);
                        segment.casingType = type;
                        segments.push(segment);
                    }
                    this.casingPlan.wellSegments = segments;
                };
            }
            const propDepth = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.ProposedTotalDepth));
            this.casingPlan.proposedTotalDepth = parseFloat(propDepth);
            let completionState = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.IsComplete));
            if (completionState === null || completionState === undefined) {
                completionState = this.affirmComplete;
                this.saveContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.IsComplete), this.affirmComplete);
            } else {
                this.affirmComplete = completionState;
            }
            if (this.affirmComplete) {
                this.currentStep = CasingPlanSteps.ReviewCasingPlan;
                this.previousStep = CasingPlanSteps.WellSegmentWizard;
                this.previousSubstep = SegmentWizardSteps.ReviewWellSegment;
                this.activeSegmentIndex = this.casingPlan.wellSegments.length;                
            }            
            this.wellDirection = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellDirection));
            this.wellType = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellType));
            this.wellTypeDescription = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellTypeDescription));
            this.nad83Y = this.getLatitudeFromContext();
            this.nad83X = this.getLongitudeFromContext();
            this.statePlane = this.getStatePlane().toLowerCase() === StatePlane.South
                ? CoordinateSystem.OHIO_SOUTH
                : CoordinateSystem.OHIO_NORTH;
            const latLong = this.location.statePlaneToWebMercator(this.nad83X, this.nad83Y, this.statePlane, false);

            this.latitude = latLong[0];
            this.longitude = latLong[1];
            if ((isDefined(this.casingPlan.surfaceLatitude) && this.casingPlan.surfaceLatitude !== this.latitude) || (isDefined(this.casingPlan.surfaceLongitude) && this.casingPlan.surfaceLongitude !== this.longitude)) {
                //Surface Location has changed, delete everything as building requirements could change due to different GIS features
                this.casingPlan.wellSegments = [];
            }
            const urban = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.UrbanArea));
            this.casingPlan.isUrbanizedArea = urban !== "NONURB";
            this.proposedFormations = this.readProposedFormations();
            this.casingPlan.surfaceLatitude = this.latitude;
            this.casingPlan.surfaceLongitude = this.longitude;
            if (!this.casingPlan.wellSegments || this.casingPlan.wellSegments.length === 0) {
                this.addNewSegment(false);
            } else {
                this.currentWellSegment = this.casingPlan.wellSegments[this.casingPlan.wellSegments.length - 1];
            }
            this.isNewWell = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.NewOrExistingWell)) === "New";
            this.casingPlan.newWell = this.isNewWell;

            this.casingPlan.wellType = this.wellType;
            this.casingPlan.wellTypeDescription = this.wellTypeDescription;

            this.alerts = this.constructionService.alerts$.subscribe((list) => {
                if (list) {
                    this.alertList = list;
                    this.alertList.sort((a, b) => {
                    let rv = 0;
                    if (a.alertType > b.alertType) {
                        rv = -1;
                    } else if (a.alertType < b.alertType) {
                        rv = 1;
                    }
                    return rv;
                    });
                    this.casingPlan.alerts = [];
                    this.alertList.forEach(a => this.casingPlan.alerts.push(new AlertStatus(a)));
                }
            });
            this.observableAlerts = this.constructionService.observableAlerts$.subscribe((alert) => {
                if (alert) {
                    const index = this.alertList.findIndex(a => a.name === alert.name);
                    if (index !== -1) { //alert is in list, its status just changed
                        this.alertList.splice(index, 1);
                    }
                    this.alertList.push(alert);
                    this.alertList.sort((a, b) => {
                        let rv = 0;
                        if (a.alertType > b.alertType) {
                            rv = -1;
                        } else if (a.alertType < b.alertType) {
                            rv = 1;
                        }
                        return rv;
                    });
                    this.casingPlan.alerts = [];
                    this.alertList.forEach(a => this.casingPlan.alerts.push(new AlertStatus(a)));
                }

            });
            this.completionEligible = this.constructionService.completionEligible$.subscribe((result) => {
            this.canBeCompleted = result;
            switch (result) {
                case "yes":
                    this.segmentQueued = undefined;
                break;
                case "no":
                    this.segmentQueued = true;
                    this.isComplete = false;
                break;
                case "required":
                    this.segmentQueued = false;
                    this.isComplete = true;
                break;
            }
            });
            this.constructionService.getConstructionAlerts(this);
        }
    }

    public activeSegmentChanged(activated: WellSegmentModel): void {
        if (this.blockAdvance) {
            console.log("Edit blocked due to save in progress.");
            return;
        }
        this.saveInProgress.emit(true);
        const index = this.casingPlan.wellSegments.findIndex(w => w.id === activated.id);
        this.activeSegmentIndex = index;
        this.currentWellSegment = this.casingPlan.wellSegments[index];
        if (this.enableEditing) {
            this.previousSubstep = (this.activeSegmentIndex !== 0) ? SegmentWizardSteps.ReviewWellSegment : undefined;
            this.substep = SegmentWizardSteps.Casing;            
        } else {
            this.previousSubstep = SegmentWizardSteps.ReviewWellSegment;
            this.substep = SegmentWizardSteps.ReviewWellSegment;
        }
        this.saveInProgress.emit(false);
        this.previousStep = (this.activeSegmentIndex === 0) ? CasingPlanSteps.AlertsOverview: CasingPlanSteps.WellSegmentWizard;
    }

    public addNewSegment(e: boolean): void {
        this.currentWellSegment = new WellSegmentModel();
        this.currentWellSegment.index = this.idx;
        this.currentWellSegment.gisObjects = [];
        if (this.casingPlan.wellSegments.length > 0) {
            this.previousWellSegment = this.casingPlan.wellSegments[this.casingPlan.wellSegments.length - 1];
        }
        if (this.previousWellSegment) {
            this.currentWellSegment.boreholeTop = this.previousWellSegment.boreholeBottom;
            const end = this.previousWellSegment.gisObjects.find(g => g.point === "End");
            if (end) {
            const start = new WellSegmentGisDataModel();
            start.md = end.md;
            start.inclination = end.inclination;
            start.azimuth = end.azimuth;
            start.tvd = end.tvd;
            start.vertSect = end.vertSect;
            start.north = end.north;
            start.east = end.east;
            start.gridEast = end.gridEast;
            start.gridNorth = end.gridNorth;
            start.latitude = end.latitude;
            start.longitude = end.longitude;
            start.closureDistance = end.closureDistance;
            start.dls = end.dls;
            start.buildRate = end.buildRate;
            start.turnRate = end.turnRate;
            start.point = "Start";
            this.currentWellSegment.gisObjects.push(start);
            }
        } else {
            this.currentWellSegment.boreholeTop = 0;
            this.currentWellSegment.casingTop = 0;
            const start = new WellSegmentGisDataModel();
            start.tvd = 0;
            start.latitude = this.latitude;
            start.longitude = this.longitude;
            this.currentWellSegment.gisObjects.push(start);
        }
        //Only add this segment if it's not already in the list
        if (this.casingPlan.wellSegments.find(a => a.id == this.currentWellSegment.id) == undefined) {
            this.casingPlan.wellSegments.push(this.currentWellSegment);
            this.activeSegmentIndex = this.casingPlan.wellSegments.length - 1;
            this.segmentChanged.emit(this.currentWellSegment);
        }
        this.segmentQueued = undefined;
    }

    public convertBoolean(value: boolean): string {
        return booleanToYesNo(value);
    }

    public deleteWarning(idx: number): void {
        if (this.blockAdvance) {
            console.log("Delete blocked due to save in progress");
            return;
        }
        if (idx < this.casingPlan.wellSegments.length - 1) {
            this.idx = idx;
            const position = this.getPosition(idx);
            this.dialogService.openDialog("yesNo", `This will delete the ${position}.  All following strings will also be deleted.  Do you wish to continue?`, "confirmDelete");
        } else {
            this.deleteWellSegment(idx);
        }
    }

    public deleteWellSegment(index: number): void {
        this.casingPlan.wellSegments.splice(index, this.casingPlan.wellSegments.length - index);
        if (this.casingPlan.wellSegments.length === 0) {
            this.previousWellSegment = null;
            this.addNewSegment(true);
        } else {
            if (this.activeSegmentIndex >= this.casingPlan.wellSegments.length) {
                if (this.currentStep === "reviewCasingPlan") {
                    this.evaluateRules();
                    this.editWellSegment(this.casingPlan.wellSegments[this.casingPlan.wellSegments.length - 1]);
                    this.saveInProgress.emit(false);
                } else {
                    this.activeSegmentIndex = this.casingPlan.wellSegments.length - 1;
                    this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
                    this.segmentChanged.emit(this.currentWellSegment);
                    this.evaluateRules();
                }
            }
        }
    }

    public editWellSegment(model: WellSegmentModel): void {
        this.currentStep = CasingPlanSteps.WellSegmentWizard;
        this.activeSegmentChanged(model);
    }

    public encodeDrawing(imageType: string): string {
        const el = document.getElementById("canvas");
        if (el) {
            const canvas = <HTMLCanvasElement>el;
            const url = canvas.toDataURL(imageType, 1.0);
            return url;
        }
        return "";
    }

    public evaluateRules(): void {
        this.constructionService.evaluateAlerts();
    }

    public ensureGisPoints(points: string[]): void {
        for (let pt = 0; pt < points.length; pt++) {
            let gisPoint = this.currentWellSegment.gisObjects.find(g => g.point === points[pt]);
            if (!gisPoint) {
                gisPoint = new WellSegmentGisDataModel();
                gisPoint.point = points[pt];
                switch (points[pt].toLowerCase()) {
                    case "start":
                        gisPoint.tvd = this.currentWellSegment.boreholeTop;
                    break;
                    case "end":
                        gisPoint.tvd = this.currentWellSegment.boreholeBottom;
                    break;
                }
                if (this.previousWellSegment) {
                    const end = this.previousWellSegment.gisObjects.find(p => p.point === "End");
                    gisPoint.latitude = end.latitude;
                    gisPoint.longitude = end.longitude;
                } else {
                    gisPoint.latitude = this.latitude;
                    gisPoint.longitude = this.longitude;
                }
                this.currentWellSegment.gisObjects.push(gisPoint);
            } else if (points[pt].toLowerCase() === "start") {
                //Ensure Start is at the Top of the current Casing, or Borehole if no casing
                const top = this.hasCasing(this.currentWellSegment) ? this.currentWellSegment.casingTop : this.currentWellSegment.boreholeTop;
                gisPoint.tvd = top;
                gisPoint.md = top;
            }
        }
    }

    public getPosition(index: number): string {
        let pos = "";
        switch (index) {
            case 0:
                pos = "Well Head";
            break;
            case 1:
                pos = "Second String";
            break;
            case 2:
                pos = "Third String";
            break;
            case 3:
                pos = "Fourth String";
            break;
            case 4:
                pos = "Fifth String";
            break;
            case 5:
                pos = "Sixth String";
            break;
            case 6:
                pos = "Seventh String";
            break;
            case 7:
                pos = "Eighth String";
            break;
            case 8:
                pos = "Ninth String";
            break;
            case 9:
                pos = "Tenth String";
            break;
        }
        return pos
    }

    public getPreviousSegment(index: number): WellSegmentModel {
        if (index === 0) return null;
        return this.casingPlan.wellSegments[index - 1];
    }

    public gisOnGridReady(params): void {
        this.gisGridApi = params.api;
        this.gisGridApi.sizeColumnsToFit();
        this.gisGridApi.refreshCells();
    }

    public goToRegulations(): void {
        this.currentStep = CasingPlanSteps.Regulations;
        this.previousStep = CasingPlanSteps.Introduction;
    }

    public hasBorehole(segment: WellSegmentModel): boolean {
        return hasBorehole(segment);
    }

    public hasCasing(segment: WellSegmentModel): boolean {
        return hasCasing(segment);
    }

    public hasCement(segment: WellSegmentModel): boolean {
        return hasCement(segment);
    }


    public objDefined(obj: any): boolean {
        return isDefined(obj);
    }

    public onFormReady(form: FormGroup): void {
        this.formGroup = form;
    }

    public queueNewSegment(event: boolean | undefined): void {
        this.segmentQueued = event;
        if (!event) {
            this.affirmComplete = false; //invalidate affirmation if they decide to add another string
        }
    }

    public readProposedFormations(): string[] {
        const formations = [];
        if (this.componentData.config.wellDataPoints) {
            const locations = getMultipleReadContextLocations(this.componentData.config.wellDataPoints, WellConfigurationIndex.ProposedFormation);
            for (let i = 0; i < locations.length; i++) {
            const data = this.getContextData(locations[i]);
            if (isDefined(data)) {
                formations.push(data);
            }
            }
        }
        return formations;
    }

    public saveCasingPlan(): Promise<any> {
        for (let s = 0; s < this.casingPlan.wellSegments.length; s++) {
            const segment = this.casingPlan.wellSegments[s];
            if (segment.casingBottom !== null && segment.casingBottom !== undefined && segment.casingTop !== null && segment.casingTop !== undefined) {
                segment.casingLength = segment.casingBottom - segment.casingTop;
            }
            if (segment.boreholeBottom !== null && segment.boreholeBottom !== undefined) {
                segment.boreholeDepth = segment.boreholeBottom;
            }
        }
        this.segmentChanged.emit(this.currentWellSegment);
        this.setContextValue(this.casingPlan);
        this.saveInProgress.emit(true);

        return this.flowComponent.promiseSaveContext().then(() => {
            this.saveInProgress.emit(false);
            console.log("context updated");
        }).catch((error) => {
            console.log(error);
        });
    }

    public saveDrawings(name: string, url: string): Promise<IAzureBlobSasToken> {
        var data = url.split(','), mime = data[0].match(/:(.*?);/)[1], bstr = atob(data[1]), n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const fileName = name.replace(" ", "_");
        let blobFile: File;
		blobFile = new File([u8arr], fileName, { type: mime });   
        const file = { fileName: `${fileName}`, rawFile: blobFile } as IUploadFile;
        return this.uploadFile(file).toPromise();
    }

    //necessary work around in order to save the images in IE/Edge
    private blobToFile(blob: Blob, fileName: string): File {
        const b: any = blob;
        b.lastModifiedDate = new Date();
        b.name = fileName;
        return b as File;
    }

    public uploadFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();
        this.documentManagementService.provision(file.rawFile.name).subscribe(x => {
            this.documentManagementService.processProvisionedFile(file, x, uploadComplete);
        }, (error) => {
            if (this.documentManagementService.WHITELIST_ERRORS.indexOf(error.error) !== -1) {
            this.dialogService.openDialog("ok", "You have uploaded a disallowed file type.  The file upload has been cancelled.  Please select a different file and try again.", "disallowed");
            }
        });
        return uploadComplete.asObservable();
    }

    public showAlert(alert: Alert): boolean {
        const isApplicable = alert.applicable;
        let appliesToStep = this.currentStep === CasingPlanSteps.ReviewCasingPlan;
        if (!appliesToStep) {
            appliesToStep = alert.showOnSteps.findIndex(s => s === this.substep) !== -1 && (alert.appliesToCasing.length === 0 || alert.appliesToCasing.findIndex(c => c === this.currentWellSegment.casingType.definition) !== -1);
        }
        return isApplicable && appliesToStep;
    }

    public sortHigher(obj): void {
        if (this.currentWellSegment.gisObjects.length <= 2) return;
        const index = this.currentWellSegment.gisObjects.findIndex(gObj => gObj === obj);
        const objArray = [];
        for (let g = 0; g < this.currentWellSegment.gisObjects.length; g++) {
            const distance = index - g;
            let gisObj: any;
            if (distance === 1) {
                gisObj = this.currentWellSegment.gisObjects[g + 1];
            } else if (distance === 0) {
                gisObj = this.currentWellSegment.gisObjects[g - 1];
            } else {
                gisObj = this.currentWellSegment.gisObjects[g];
            }
            objArray.push(gisObj);
        }
        this.currentWellSegment.gisObjects = objArray;
    }

    public sortLower(obj): void {
        if (this.currentWellSegment.gisObjects.length <= 2) return;
        const index = this.currentWellSegment.gisObjects.findIndex(gObj => gObj === obj);
        const objArray = [];
        for (let g = 0; g < this.currentWellSegment.gisObjects.length; g++) {
            const distance = index - g;
            let gisObj: any;
            if (distance === -1) {
                gisObj = this.currentWellSegment.gisObjects[g - 1];
            } else if (distance === 0) {
                gisObj = this.currentWellSegment.gisObjects[g + 1];
            } else {
                gisObj = this.currentWellSegment.gisObjects[g];
            }
            objArray.push(gisObj);
        }
        this.currentWellSegment.gisObjects = objArray;
    }

    public viewWellSegment(index: number): void {
        this.activeSegmentIndex = index;
        this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
        this.currentStep = CasingPlanSteps.WellSegmentWizard;
    }


    public getLatitudeFromContext(): number {
        if (this.isEdit) return 0;
        const direction = this.wellDirection || "";
        switch (direction) {
            case WellDirection.Directional:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeDirectional) || ""));
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeHorizontal) || ""));
            case WellDirection.Vertical:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeVertical) || ""));
            default:
                return 0;
        }
    }

    public getLongitudeFromContext(): number {
        if (this.isEdit) return 0;
        const direction = this.wellDirection || "";
        switch (direction) {
            case WellDirection.Directional:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeDirectional) || ""));
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeHorizontal) || ""));
            case WellDirection.Vertical:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeVertical) || ""));
            default:
                return 0;
        }
    }

    public getStatePlane(): string {
        if (this.isEdit) return StatePlane.South;
        const direction = this.wellDirection || "";
        let plane = StatePlane.South;
        switch (direction) {
            case WellDirection.Directional:
            plane = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneDirectional));
            break;
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
            plane = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneHorizontal));
            break;
            case WellDirection.Vertical:
            plane = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneVertical));
            break;
            default:
            break;
        }
    return plane;
    }

	public stringCanBeAngled(casing: CasingType | string): boolean {
        let canAngle: boolean = true;
        if (this.wellDirection && this.wellDirection === WellDirection.Vertical) {
            canAngle = false;
        }
        if (canAngle) {
            const unangledStrings = [CasingType.Conductor, CasingType.DrivePipe, CasingType.MineString];
            canAngle = unangledStrings.findIndex(s => s === casing) === -1;
        }
        return canAngle;
    }

    protected _decrementFunction(button: Button): void {
        if (this.currentStep == CasingPlanSteps.Introduction) {
            this.buttonOverride.callOriginalFunction(button);
        } else {
            if (this.enableEditing) {
                this.goPrevious();
            } else {
                this.reviewPrevious();
            }
    }
    }

    protected _incrementFunction(button: Button): void {
        if (this.currentStep == CasingPlanSteps.ReviewCasingPlan) {
            const drawing = this.encodeDrawing("image/png");
            if (drawing && drawing !== "") {
                this.loader.toggleLoader(true);
                this.saveDrawings(`${this.flowComponent.workflow.id}_schematic.png`, drawing).then((target) => {
                        this.casingPlan.schematic = target.blobUri;
                        this.saveCasingPlan().then((result) => {
                        this.loader.toggleLoader(false);
                        this.buttonOverride.callOriginalFunction(button);
                    }, (reject) => {
                        console.log(reject);
                    });
                }, (reject) => {
                    this.loader.toggleLoader(false);
                    console.log(reject);
                });
            } else {
                this.buttonOverride.callOriginalFunction(button);
            }
        } else {
            this.saveInProgress.emit(true);
            if (this.enableEditing) {
                this.advanceToNextStep();
            } else {
                this.reviewNext();
            }
        }
    }

    public advanceToNextStep(): void {
        let promise: Promise<any>;
        this.blockAdvance = true;
        this.previousStep = this.currentStep;
        this.previousSubstep = this.substep;
        switch (this.previousStep) {
            case CasingPlanSteps.Introduction:
                this.activeSegmentIndex = 0;
                this.currentStep = CasingPlanSteps.AlertsOverview;
                if (this.casingPlan.wellSegments.length === 0) {
                    this.addNewSegment(false);
                }
                this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
            break;
            case CasingPlanSteps.Regulations:
                this.currentStep = CasingPlanSteps.Introduction;
            break;
            case CasingPlanSteps.AlertsOverview:
                this.currentStep = CasingPlanSteps.WellSegmentWizard;
                this.substep = SegmentWizardSteps.Casing;
            break;
            case CasingPlanSteps.WellSegmentWizard:
                switch (this.substep) {
                    case SegmentWizardSteps.Casing:
                        if (this.currentWellSegment.casingType.definition === CasingType.OpenHole) {
                            this.substep = SegmentWizardSteps.Tools;
                        } else if (this.currentWellSegment.casingType.definition === CasingType.Tubing || this.currentWellSegment.casingType.definition === CasingType.Liner1) {
                            this.substep = SegmentWizardSteps.Points;
                        } else {
                            this.substep = SegmentWizardSteps.Conditions;
                        }
                        this.currentStep = CasingPlanSteps.WellSegmentWizard;
                        this.constructionService.checkCompletionEligibility(this.currentWellSegment.casingType.code);
                    break;

                    case SegmentWizardSteps.Conditions:
                        this.substep = SegmentWizardSteps.Tools;
                        if (this.currentWellSegment.casingType.definition === CasingType.DrivePipe) {
                            this.substep = SegmentWizardSteps.Points;
                        }
                        this.currentStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                    case SegmentWizardSteps.Tools:
                        this.substep = SegmentWizardSteps.Points;
                        this.currentStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                    case SegmentWizardSteps.Points:
                        if (this.requireAngles) {
                            this.substep = SegmentWizardSteps.AngleBuilder;
                        } else if (this.stringCanBeAngled(this.currentWellSegment.casingType.definition)) {
                            this.substep = SegmentWizardSteps.Angles;
                        } else {
                            this.substep = SegmentWizardSteps.ReviewWellSegment;
                        }
                        if (this.substep === SegmentWizardSteps.Angles) {
                            if (this.currentWellSegment.hasAngles === null || this.currentWellSegment.hasAngles === undefined) {
                            this.currentWellSegment.hasAngles = true;
                            }
                            this.ensureGisPoints(["Start"]);
                        } else if (this.substep === SegmentWizardSteps.AngleBuilder) {
                            this.currentWellSegment.hasAngles = true;
                            this.ensureGisPoints(["Start"]);
                        } else {
                            this.ensureGisPoints(["Start", "End"]);
                        }
                        this.currentStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                    case SegmentWizardSteps.Angles:
                        this.currentStep = CasingPlanSteps.WellSegmentWizard;
                        if (this.currentWellSegment.hasAngles) {
                            this.substep = SegmentWizardSteps.AngleBuilder;
                        } else {
                            this.substep = SegmentWizardSteps.ReviewWellSegment;
                            this.ensureGisPoints(["End"]);
                        }
                    break;
                    case SegmentWizardSteps.AngleBuilder:
                        this.currentStep = CasingPlanSteps.WellSegmentWizard;
                        this.substep = SegmentWizardSteps.ReviewWellSegment;

                    break;
                    case SegmentWizardSteps.ReviewWellSegment:
                        const drawing = this.encodeDrawing("image/png");
                        if (drawing && drawing !== "") {
                            promise = this.saveDrawings(`${this.flowComponent.workflow.id}_${this.currentWellSegment.id}_${this.currentWellSegment.casingType.definition}_detail.png`, drawing).then((target) => {
                                this.currentWellSegment.detailDiagram = target.blobUri;
                                this.goToNextSegment();
                                this.saveCasingPlan().then((result) => {
                                    this.evaluateRules();
                                    this.blockAdvance = false;
                                }, (reject) => {
                                    console.log(reject);
                                    this.blockAdvance = false;
                                });
                            });
                        } else {
                            this.goToNextSegment();
                        }
                    break;
                }
            break;
            default:
                this.currentStep = CasingPlanSteps.ReviewCasingPlan;
            break;
        }
        if (!isDefined(promise)) {
            promise = this.saveCasingPlan().then((result) => {
                this.evaluateRules();
                this.blockAdvance = false;
            }, (reject) => {
                console.log(reject);
                this.blockAdvance = false;
            });
        }
        promise.then((result) => {
            console.log("promise complete, advancing");            
            this.blockAdvance = false;
        }, (reject) => {
            console.log(reject);
            this.blockAdvance = false;
        });
    }

    public goToNextSegment(): void {
        this.currentStep = CasingPlanSteps.WellSegmentWizard;
        this.substep = SegmentWizardSteps.Casing;
        if (this.activeSegmentIndex === this.casingPlan.wellSegments.length - 1) {
            this.blockAdvance = true;
            if (this.segmentQueued || this.canBeCompleted === "no") {
                this.addNewSegment(true);                
            } else {
                this.activeSegmentIndex = this.activeSegmentIndex + 1;                
                this.currentStep = CasingPlanSteps.ReviewCasingPlan;                
            }
            this.blockAdvance = false;
            this.saveInProgress.emit(false);           
        } else {
            this.activeSegmentIndex = this.activeSegmentIndex + 1;
            this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
        }
    }

    public goPrevious(): void {
        this.blockAdvance = false;
        this.currentStep = this.previousStep;
        this.substep = (this.previousSubstep === undefined) ? SegmentWizardSteps.Casing : this.previousSubstep;
        switch (this.currentStep) {
            case CasingPlanSteps.Regulations:
                this.previousStep = CasingPlanSteps.Introduction;
                break;
            case CasingPlanSteps.AlertsOverview:
                this.previousStep = CasingPlanSteps.Introduction;
                break;
            case CasingPlanSteps.WellSegmentWizard:
                switch (this.substep) {
                    case SegmentWizardSteps.Casing:
                    if (this.activeSegmentIndex === 0) {
                        this.previousStep = CasingPlanSteps.AlertsOverview;
                        this.previousSubstep = undefined;
                    } else {
                        this.previousStep = CasingPlanSteps.WellSegmentWizard;
                        this.previousSubstep = SegmentWizardSteps.ReviewWellSegment;
                    }
                    break;
                case SegmentWizardSteps.Conditions:
                    this.previousSubstep = SegmentWizardSteps.Casing;
                    this.previousStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                case SegmentWizardSteps.Tools:
                    if (this.currentWellSegment.casingType.definition === CasingType.OpenHole) {
                        this.previousSubstep = SegmentWizardSteps.Casing;
                    } else {
                        this.previousSubstep = SegmentWizardSteps.Conditions;
                    }
                    this.previousStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                case SegmentWizardSteps.Points:
                    this.previousSubstep = SegmentWizardSteps.Tools;
                    if (this.currentWellSegment.casingType.definition === CasingType.DrivePipe) {
                        this.previousSubstep = SegmentWizardSteps.Conditions;
                    } else if (this.currentWellSegment.casingType.definition === CasingType.Liner1 || this.currentWellSegment.casingType.definition == CasingType.Tubing) {
                        this.previousSubstep = SegmentWizardSteps.Casing;
                    }
                    this.constructionService.checkCompletionEligibility(this.currentWellSegment.casingType.code);
                    this.previousStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                case SegmentWizardSteps.Angles:
                    this.previousSubstep = SegmentWizardSteps.Points;
                    this.previousStep = CasingPlanSteps.WellSegmentWizard;
                    this.constructionService.checkCompletionEligibility(this.currentWellSegment.casingType.code);
                    break;
                    case SegmentWizardSteps.AngleBuilder:
                    this.previousSubstep = (this.requireAngles) ? SegmentWizardSteps.Points : SegmentWizardSteps.Angles;
                    this.constructionService.checkCompletionEligibility(this.currentWellSegment.casingType.code);
                    this.previousStep = CasingPlanSteps.WellSegmentWizard;
                    break;
                case SegmentWizardSteps.ReviewWellSegment:
                    if (this.requireAngles) {
                        this.previousSubstep = SegmentWizardSteps.AngleBuilder;
                    } else if (this.stringCanBeAngled(this.currentWellSegment.casingType.definition)) {
                        this.previousSubstep = SegmentWizardSteps.Angles;
                    } else {
                        this.previousSubstep = SegmentWizardSteps.Points;
                    }
                    this.previousStep = CasingPlanSteps.WellSegmentWizard;
                    this.activeSegmentIndex = this.activeSegmentIndex - 1;
                    this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
                    this.segmentChanged.emit(this.currentWellSegment);
                    this.constructionService.checkCompletionEligibility(this.currentWellSegment.casingType.code);
                break;
            }
            break;
        }
        setTimeout(() => { this.saveInProgress.emit(false); }, 1000);
    }

    public reviewNext(): void {
        this.previousStep = this.currentStep;
        switch (this.previousStep) {
            case CasingPlanSteps.Introduction:
            this.activeSegmentIndex = 0;
            this.currentStep = CasingPlanSteps.AlertsOverview;
            this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
            break;
            case CasingPlanSteps.Regulations:
            this.currentStep = CasingPlanSteps.Introduction;
            break;
            case CasingPlanSteps.AlertsOverview:
            this.currentStep = CasingPlanSteps.WellSegmentWizard;
            this.substep = SegmentWizardSteps.ReviewWellSegment;
            break;
            case CasingPlanSteps.WellSegmentWizard:
            if (this.activeSegmentIndex === this.casingPlan.wellSegments.length - 1) {
                this.currentWellSegment = null;
                this.currentStep = CasingPlanSteps.ReviewCasingPlan;
            } else {
                this.activeSegmentIndex = this.activeSegmentIndex + 1;
                this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
                //no change to step or substep, want to go to ReviewWellSegments of the next string
            }
            break;
        }
    }

    public reviewPrevious(): void {
        switch (this.currentStep) {
            case CasingPlanSteps.Regulations:
            this.previousStep = CasingPlanSteps.Introduction;
            break;
            case CasingPlanSteps.AlertsOverview:
            this.previousStep = CasingPlanSteps.Introduction;
            break;
            case CasingPlanSteps.WellSegmentWizard:
            if (this.activeSegmentIndex === 0) {
                this.previousStep = CasingPlanSteps.AlertsOverview;
            } else { //Stay in WellSegmentWizard, reversing amongst ReviewWellSegment pages
                this.activeSegmentIndex = this.activeSegmentIndex - 1;
                this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
            }
            break;
            case CasingPlanSteps.ReviewCasingPlan:
            this.activeSegmentIndex = this.casingPlan.wellSegments.length - 1;
            this.currentWellSegment = this.casingPlan.wellSegments[this.activeSegmentIndex];
            this.previousStep = CasingPlanSteps.WellSegmentWizard;
            this.substep = SegmentWizardSteps.ReviewWellSegment;
            break;
        }
        this.currentStep = this.previousStep;
    }

    public dialogConfirmed(): void {
        this.dialogService.closeDialog();
        if (this.dialogService.dialogId === 'confirmDelete') {
            this.deleteWellSegment(this.idx);
        } else if (this.dialogService.dialogId === 'disallowed') {
            //Do nothing
        }
    }

    public onDialogClose(): void {
        this.dialogService.closeDialog();
        console.log("Delete cancelled");
    }

}
